import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Key, Copy, Plus, Terminal, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MCPKeysPage() {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('claude');

  const fetchKeys = useCallback(async () => {
    try { const res = await fetch(`${API}/api/mcp/keys`, { credentials: 'include' }); if (res.ok) { const d = await res.json(); setKeys(d.keys || []); } } catch (e) { console.error("MCP keys fetch error:", e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const generateKey = async () => {
    try {
      const res = await fetch(`${API}/api/mcp/keys/generate`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      const d = await res.json();
      setNewKey(d.key);
      fetchKeys();
      toast.success('MCP key generated!');
    } catch (e) { toast.error(e.message); }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this key? Any tools using it will stop working.')) return;
    await fetch(`${API}/api/mcp/keys/${keyId}`, { method: 'DELETE', credentials: 'include' });
    fetchKeys();
    toast.success('Key revoked');
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const claudeConfig = `{
  "mcpServers": {
    "sendpi": {
      "command": "npx",
      "args": ["-y", "sendpi-mcp-server"],
      "env": {
        "SENDPI_API_KEY": "${newKey || 'sk-sendpi-YOUR-KEY-HERE'}"
      }
    }
  }
}`;

  return (
    <div data-testid="mcp-keys-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>MCP & API Access</h1>
          <p className="text-white/40 text-sm mt-1">Connect SendPI to Claude, ChatGPT, Cursor, and any AI tool</p>
        </div>
        <Link to="/docs/mcp" target="_blank" className="flex items-center gap-1.5 text-violet-400 text-xs hover:underline">
          <ExternalLink className="w-3.5 h-3.5" /> View Docs
        </Link>
      </div>

      {/* New key alert */}
      {newKey && (
        <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl" data-testid="new-key-alert">
          <p className="text-violet-300 text-xs font-bold mb-2">Copy this key now — you will not see it again.</p>
          <div className="flex items-center gap-2 bg-black/30 rounded-lg px-4 py-3">
            <code className="text-emerald-400 text-sm flex-1 font-mono break-all">{newKey}</code>
            <button onClick={() => copyText(newKey)} className="text-white/50 hover:text-white shrink-0"><Copy className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Keys section */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold text-sm flex items-center gap-2"><Key className="w-4 h-4 text-violet-400" /> Your API Keys</h2>
          <button onClick={generateKey} className="px-3 py-1.5 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-300 text-xs hover:bg-violet-500/30 flex items-center gap-1.5" data-testid="generate-key-btn">
            <Plus className="w-3 h-3" /> Generate Key
          </button>
        </div>
        {loading ? <p className="text-white/30 text-sm">Loading...</p> : keys.length === 0 ? (
          <div className="text-center py-8">
            <Key className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm">No MCP keys yet. Generate one to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.id} className="flex items-center gap-3 bg-white/[0.03] rounded-lg px-4 py-3 border border-white/[0.06]" data-testid={`mcp-key-${k.id}`}>
                <Terminal className="w-4 h-4 text-white/20 shrink-0" />
                <code className="text-white/60 text-xs font-mono">{k.key_prefix}</code>
                <span className="text-white/20 text-[10px]">Created {k.created_at?.slice(0, 10)}</span>
                <span className="text-white/20 text-[10px]">Last used: {k.last_used?.slice(0, 10) || 'never'}</span>
                <span className="ml-auto text-white/30 text-[10px] font-medium">{k.calls_today}/{k.tier_limit} today</span>
                <button onClick={() => revokeKey(k.id)} className="text-red-400/50 hover:text-red-400 text-xs font-medium">Revoke</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tier limits */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-semibold text-sm mb-3">Rate Limits</h2>
        <div className="grid grid-cols-3 gap-3">
          {[{ tier: 'Free', limit: '100/day', active: true }, { tier: 'Pro', limit: '2,000/day' }, { tier: 'Max', limit: 'Unlimited' }].map(t => (
            <div key={t.tier} className={`rounded-lg p-3 text-center ${t.active ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-white/[0.03] border border-white/[0.06]'}`}>
              <p className={`text-sm font-bold ${t.active ? 'text-violet-400' : 'text-white/50'}`}>{t.tier}</p>
              <p className="text-white/40 text-xs mt-0.5">{t.limit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Install instructions */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-semibold text-sm mb-4">Setup Instructions</h2>
        <div className="flex gap-2 mb-4">
          {['claude', 'cursor', 'chatgpt'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tab === t ? 'bg-violet-500/20 text-violet-300' : 'bg-white/5 text-white/40 hover:text-white/60'}`}>
              {t === 'claude' ? 'Claude Desktop' : t === 'cursor' ? 'Cursor' : 'ChatGPT'}
            </button>
          ))}
        </div>

        {tab === 'claude' && (
          <div className="space-y-3">
            <p className="text-white/50 text-sm">1. Generate an API key above</p>
            <p className="text-white/50 text-sm">2. Add to your Claude Desktop config file:</p>
            <div className="relative">
              <pre className="bg-black/40 rounded-lg p-4 text-[11px] text-emerald-400/80 font-mono overflow-x-auto border border-white/[0.06]">{claudeConfig}</pre>
              <button onClick={() => copyText(claudeConfig)} className="absolute top-3 right-3 text-white/30 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-white/30 text-xs">File: ~/Library/Application Support/Claude/claude_desktop_config.json</p>
            <p className="text-white/50 text-sm">3. Restart Claude Desktop</p>
            <p className="text-white/50 text-sm">4. Look for SendPI tools in your Claude sidebar</p>
          </div>
        )}

        {tab === 'cursor' && (
          <div className="space-y-3">
            <p className="text-white/50 text-sm">1. Generate an API key above</p>
            <p className="text-white/50 text-sm">2. In Cursor Settings → MCP → Add server:</p>
            <div className="bg-black/40 rounded-lg p-4 text-[11px] text-white/60 font-mono border border-white/[0.06] space-y-1">
              <p>Name: <span className="text-emerald-400">SendPI</span></p>
              <p>Command: <span className="text-emerald-400">npx -y sendpi-mcp-server</span></p>
              <p>Environment: <span className="text-emerald-400">SENDPI_API_KEY={newKey || 'sk-sendpi-YOUR-KEY-HERE'}</span></p>
            </div>
            <p className="text-white/50 text-sm">3. Reload Cursor</p>
            <p className="text-white/50 text-sm">4. SendPI tools available in Agent mode</p>
          </div>
        )}

        {tab === 'chatgpt' && (
          <div className="space-y-3">
            <p className="text-white/50 text-sm">Use the SendPI Custom GPT or connect via Actions API:</p>
            <div className="flex gap-3">
              <a href="/docs/mcp" target="_blank" className="px-4 py-2 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-300 text-xs hover:bg-violet-500/30">Open MCP Docs</a>
              <a href={`${API}/api/openai/openapi.json`} target="_blank" className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/50 text-xs hover:text-white/70">ChatGPT Actions Spec</a>
            </div>
            <p className="text-white/30 text-xs mt-2">Use the OpenAPI spec URL above when creating a Custom GPT with Actions.</p>
          </div>
        )}
      </div>

      {/* 5 Available Tools */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-white font-semibold text-sm mb-4">Available MCP Tools</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { name: 'pi_check', desc: 'Predict receiver emotion before send', color: '#A855F7' },
            { name: 'pi_cast', desc: 'Generate precision cast from plain English', color: '#22D3EE' },
            { name: 'optimize_tokens', desc: 'Reduce tokens 40-70% keeping quality', color: '#10B981' },
            { name: 'search_casts', desc: 'Search 4.3M precision casts', color: '#F59E0B' },
            { name: 'pi_loop', desc: 'Auto-refine until quality threshold met', color: '#EC4899' },
          ].map(t => (
            <div key={t.name} className="bg-white/[0.03] rounded-lg p-3 border border-white/[0.06]">
              <div className="w-2 h-2 rounded-full mb-2" style={{ backgroundColor: t.color }} />
              <p className="text-white text-xs font-mono font-bold">{t.name}</p>
              <p className="text-white/35 text-[11px] mt-1">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
